import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { ArrowLeft, Loader2 } from "lucide-react";
import { showError, showSuccess, showErrorWithApiError } from "@/shared/utils/toast-utils";
import { ERROR, SUCCESS } from "@/config/toast-message-config";
import { useVerifyOTP, useSendOTP } from "@/app/auth/services/auth";
import { useUser } from "@/contexts/UserContext";
import orbiusLogo from "@/assets/orbius_logo.png";
import launchBetterImage from "@/assets/Launch_better.jpg";
import { ROUTES } from "@/config/routeConfig";
import type { LoginChallenge, MfaFactor } from "@/shared/types/authTypes";
import { resolveLoginChallenge } from "@/app/auth/utils/loginChallenge";

enum VerifyOTPState {
  VERIFYING_OTP = 'verifying_otp',
  INVALID_OTP = 'invalid_otp',
  OTP_VERIFIED = 'otp_verified'
}

const EMAIL_ONLY_CHALLENGE: LoginChallenge = {
  methods: ['EMAIL_OTP'],
  preferred: 'EMAIL_OTP',
  emailOtp: { sent: true },
  allowEmailFallback: false,
  allowRecovery: false,
};

const factorCopy: Record<MfaFactor, { title: string; description: string; label: string; hint: string }> = {
  EMAIL_OTP: {
    title: 'Verify Your Identity',
    description: "We've sent a verification code to your email. Please enter the code below.",
    label: 'Verification Code',
    hint: 'Enter the 6-digit code sent to your email',
  },
  TOTP: {
    title: 'Two-factor authentication',
    description: 'Enter the 6-digit code from your authenticator app.',
    label: 'Authenticator code',
    hint: 'Google Authenticator, Microsoft Authenticator, Authy, 1Password, and similar apps',
  },
  RECOVERY: {
    title: 'Recovery code',
    description: 'Enter one of your unused recovery codes.',
    label: 'Recovery code',
    hint: 'Codes look like 8F3K-92LP',
  },
};

const sanitizeCode = (factor: MfaFactor, value: string): string => {
  if (factor === 'RECOVERY') {
    return value.replace(/[^a-zA-Z0-9-]/g, '').slice(0, 12).toUpperCase();
  }
  return value.replace(/\D/g, '').slice(0, 6);
};

const isCodeComplete = (factor: MfaFactor, code: string): boolean => {
  if (factor === 'RECOVERY') {
    return code.replace(/-/g, '').length >= 8;
  }
  return code.length === 6;
};

const VerifyOTPPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { completeLogin } = useUser();
  const authId = location.state?.authId as string | undefined;
  const initialChallenge: LoginChallenge =
    resolveLoginChallenge({
      authId,
      challenge: location.state?.challenge,
    }) ?? EMAIL_ONLY_CHALLENGE;

  const verifyOTP = useVerifyOTP();
  const sendOTP = useSendOTP();

  const [challenge, setChallenge] = useState<LoginChallenge>(initialChallenge);
  const [activeFactor, setActiveFactor] = useState<MfaFactor>(initialChallenge.preferred);
  const [code, setCode] = useState("");
  const [verifyOTPState, setVerifyOTPState] = useState<VerifyOTPState>(VerifyOTPState.VERIFYING_OTP);
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (!authId) {
      showError(ERROR.OTP.MISSING_PARAMS);
      const redirectTimeoutId = setTimeout(() => {
        navigate(ROUTES.LOGIN, { replace: true });
      }, 2000);
      return () => clearTimeout(redirectTimeoutId);
    }

    if (initialChallenge.preferred === 'EMAIL_OTP') {
      setCountdown(30);
    }
  }, [authId, navigate, initialChallenge.preferred]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const switchFactor = (factor: MfaFactor) => {
    setActiveFactor(factor);
    setCode("");
    setVerifyOTPState(VerifyOTPState.VERIFYING_OTP);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isCodeComplete(activeFactor, code)) {
      showError(ERROR.OTP.MISSING_OTP);
      return;
    }

    if (!authId) {
      showError(ERROR.OTP.MISSING_PARAMS);
      return;
    }

    try {
      await verifyOTP.mutateAsync({
        authId,
        code,
        factor: activeFactor,
      });

      setVerifyOTPState(VerifyOTPState.OTP_VERIFIED);
      showSuccess(SUCCESS.OTP.VERIFIED);
      await completeLogin();
    } catch (error) {
      showErrorWithApiError(error, ERROR.OTP.INVALID_OTP);
      setVerifyOTPState(VerifyOTPState.INVALID_OTP);
    }
  };

  const requestEmailBackup = async (isResend: boolean) => {
    if (!authId) {
      showError(ERROR.OTP.MISSING_PARAMS);
      return;
    }
    if (isResend && countdown > 0) {
      return;
    }

    setIsResending(true);
    try {
      const loginData = await sendOTP.mutateAsync({ authId });
      const nextChallenge = resolveLoginChallenge(
        loginData
        ?? (loginData?.challenge
          ? { authId, challenge: loginData.challenge }
          : { authId, challenge: { methods: ['EMAIL_OTP'], preferred: 'EMAIL_OTP', emailOtp: { sent: true } } }),
      );
      if (nextChallenge) {
        setChallenge(nextChallenge);
      } else {
        setChallenge((current) => ({
          ...current,
          emailOtp: { sent: true },
        }));
      }
      switchFactor('EMAIL_OTP');
      setCountdown(60);
      showSuccess(isResend ? SUCCESS.OTP.RESENT : SUCCESS.OTP.EMAIL_BACKUP_SENT);
    } catch (error) {
      showErrorWithApiError(error, ERROR.OTP.RESEND_FAILED);
    } finally {
      setIsResending(false);
    }
  };

  const handleBackToLogin = () => {
    navigate(ROUTES.LOGIN, { replace: true });
  };

  const copy = factorCopy[activeFactor];
  const showEmailBackup = challenge.allowEmailFallback && activeFactor !== 'EMAIL_OTP';
  const showAuthenticatorSwitch = challenge.methods.includes('TOTP') && activeFactor !== 'TOTP';
  const showRecovery = challenge.allowRecovery && activeFactor !== 'RECOVERY';
  const showResend = activeFactor === 'EMAIL_OTP';

  return (
    <>
      <div className="relative min-h-screen overflow-hidden bg-muted">
        <img
          src={launchBetterImage}
          alt="Launch Better"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover object-right -scale-x-100"
        />
        <div className="relative z-10 flex min-h-screen w-full flex-col p-5 gap-5">
          <div className="flex w-full items-center justify-between">
            <img src={orbiusLogo} alt="Orbius Logo" className="h-16 w-auto drop-shadow-md" />

            <Button
              variant="outline"
              onClick={() => window.open('mailto:support@orbius.com', '_blank')}
            >
              Contact us
            </Button>
          </div>
          <div className="grid grid-cols-2 flex-1 items-center justify-center">
            <div className="w-full h-full">
            </div>
            <div className="flex items-center justify-center w-full h-full">
              <Card className="w-full max-w-md border shadow-sm">
                <CardHeader>
                  <CardTitle>
                    {copy.title}
                  </CardTitle>
                  <CardDescription>
                    {copy.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="code" className="text-sm font-medium">
                        {copy.label}
                      </Label>
                      <Input
                        id="code"
                        type="text"
                        inputMode={activeFactor === 'RECOVERY' ? 'text' : 'numeric'}
                        autoComplete={activeFactor === 'TOTP' ? 'one-time-code' : 'off'}
                        placeholder={activeFactor === 'RECOVERY' ? 'XXXX-XXXX' : 'Enter 6-digit code'}
                        value={code}
                        onChange={(e) => setCode(sanitizeCode(activeFactor, e.target.value))}
                        className="w-full text-center text-lg tracking-widest"
                        maxLength={activeFactor === 'RECOVERY' ? 12 : 6}
                        required
                      />
                      <p className="text-xs text-center">
                        {copy.hint}
                      </p>
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-primary hover:bg-primary/90"
                      disabled={verifyOTP.isPending || verifyOTPState === VerifyOTPState.OTP_VERIFIED}
                    >
                      {verifyOTP.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Verifying...
                        </>
                      ) : verifyOTPState === VerifyOTPState.OTP_VERIFIED ? (
                        "Verified ✓"
                      ) : (
                        "Verify Code"
                      )}
                    </Button>
                  </form>

                  <div className="mt-6 pt-6 border-t border-border space-y-3">
                    {showResend && (
                      <div className="text-center">
                        <Button
                          variant="ghost"
                          className="w-full text-sm"
                          onClick={() => requestEmailBackup(true)}
                          disabled={isResending || countdown > 0}
                        >
                          {isResending ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Sending...
                            </>
                          ) : countdown > 0 ? (
                            `Resend code in ${countdown}s`
                          ) : (
                            "Didn't receive the code? Resend"
                          )}
                        </Button>
                      </div>
                    )}

                    {showEmailBackup && (
                      <div className="text-center">
                        <Button
                          variant="ghost"
                          className="w-full text-sm"
                          onClick={() => requestEmailBackup(false)}
                          disabled={isResending}
                        >
                          {isResending ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Sending email code...
                            </>
                          ) : (
                            "Use email code instead"
                          )}
                        </Button>
                      </div>
                    )}

                    {showAuthenticatorSwitch && (
                      <div className="text-center">
                        <Button
                          variant="ghost"
                          className="w-full text-sm"
                          onClick={() => switchFactor('TOTP')}
                        >
                          Use authenticator instead
                        </Button>
                      </div>
                    )}

                    {showRecovery && (
                      <div className="text-center">
                        <Button
                          variant="ghost"
                          className="w-full text-sm"
                          onClick={() => switchFactor('RECOVERY')}
                        >
                          Use a recovery code
                        </Button>
                      </div>
                    )}

                    <div className="text-center">
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={handleBackToLogin}
                      >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back to Login
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VerifyOTPPage;
